import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSurveyStore } from "./surveyutilities/surveyStore";
import { Box, Paper, Typography } from '@mui/material';

// Parts
import SurveyHeader from "./viewerparts/SurveyHeader";
import ProgressBar from "./viewerparts/ProgressBar";
import Navigation from "./viewerparts/Navigation";
import SubmitMessage  from "./viewerparts/SubmitMessage.jsx";
// Question Blocks
import TextQuestionBlock from "./viewerparts/questionBlocks/TextQuestionBlock";
import TextAreaQuestionBlock from "./viewerparts/questionBlocks/TextAreaQuestionBlock";
import DropdownQuestionBlock from "./viewerparts/questionBlocks/DropdownQuestionBlock";
import SingleChoiceQuestionBlock from "./viewerparts/questionBlocks/SingleChoiceQuestionBlock";
import MultipleChoiceQuestionBlock from "./viewerparts/questionBlocks/MultipleChoiceQuestionBlock";
import RatingQuestionBlock from "./viewerparts/questionBlocks/RatingQuestionBlock";

const EcxSurveyViewer = ({ theme }) => {
    const survey = useSurveyStore(state => state.survey);
    const questions = useSurveyStore(state => state.questions);
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        setValue,
        formState: { errors },
    } = useForm();

    const [formValues, setFormValues] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [visitedPages, setVisitedPages] = useState(new Set());
    const [isSubmitted,setIsSubmitted] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);
    // Watch values
    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name) {
                setFormValues((prev) => ({
                    ...prev,
                    [name]: value[name],
                }));
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);



    const onSubmit = (data,e) => {
        e?.preventDefault();
        const isLastPage = currentPage === questions.length - 1;
        const showSinglePage = survey.singleQuestionPerPage === "true";

        if (isLastPage || !showSinglePage) {
            console.log("Final Submit", data);
            // Store submitted data and set submitted state
            setSubmittedData(data);
            setIsSubmitted(true);
        }


    };

    const isCurrentPageValid = async () => {
        if (!questions?.[currentPage]?.required) return true;
        return await trigger(questions[currentPage].internalId);
    };

    const handleNext = async () => {
        console.log("Current page:", currentPage);
        console.log("Next page:", currentPage + 1);
        const isValid = await isCurrentPageValid();
        if (isValid) {
            setVisitedPages((prev) => new Set([...prev, currentPage]));
            setCurrentPage((prev) =>
                Math.min(prev + 1, questions.length - 1)
            );
        }
    };

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 0));
    };

    const calculateProgress = () => {
        if (!questions?.length) return 0;
        if (survey.progressbarType === "STEPS") {
            return {
                current: currentPage + 1,
                total: questions.length,
            };
        }
        return Math.round((currentPage / questions.length) * 100);
    };

    const renderQuestion = (q) => {
        const commonProps = { question: q, register, errors, watch };

        switch (q.type) {
            case "text":
                return <TextQuestionBlock {...commonProps} />;
            case "textarea":
                return <TextAreaQuestionBlock {...commonProps} />;
            case "dropdown":
                return <DropdownQuestionBlock {...commonProps} />;
            case "single_choice":
                return <SingleChoiceQuestionBlock {...commonProps} />;
            case "multiple_choice":
                return <MultipleChoiceQuestionBlock {...commonProps} />;
            case "rating":
                return <RatingQuestionBlock {...commonProps} />;
           /* case "file_upload":
                return (
                    <FileUploadQuestionBlock
                        {...commonProps}
                        uploadedFiles={uploadedFiles}
                        onFileUpload={(id, e) => handleFileUpload(id, e)}
                        onRemoveFile={(id, name) => removeFile(id, name)}
                    />
                );*/
            default:
                return null;
        }
    };

    if (!survey) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status" />
                <p className="mt-3">Loading survey...</p>
            </div>
        );
    }
    // Show thank you message if survey is submitted
    if (isSubmitted) {
        return <SubmitMessage survey={survey} submittedData={submittedData} />;
    }
    const showSinglePage = survey.singleQuestionPerPage;

    const currentQuestion = showSinglePage
        ? questions[currentPage]
        : null;

    return (
        <Box sx={{ backgroundColor: '#e0e0e0', borderRadius: '10px', p: '50px', minHeight: '90vh' }}>
            <div className="row">
                {/* Survey Form - 70% */}
                <div className="col-8">
                    <Paper sx={{
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        margin: '0 auto',
                        minHeight: '500px',
                        backgroundColor: 'white',
                        position: 'relative',
                        p: 3,
                        borderRadius: '10px'
                    }}>
                        <SurveyHeader survey={survey} theme={theme} />
                        {showSinglePage && (
                            <ProgressBar survey={survey} progress={calculateProgress()} />
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            {showSinglePage ? (
                                <div>
                                    {currentQuestion && (
                                        <div key={currentQuestion.id}>{renderQuestion(currentQuestion)}</div>
                                    )}
                                    <Navigation
                                        isFirstPage={currentPage === 0}
                                        isLastPage={currentPage === questions.length - 1}
                                        onPrevious={handlePrevious}
                                        onNext={handleNext}
                                    />
                                </div>
                            ) : (
                                <div>
                                    {questions?.map((q) => (
                                        <div key={q.id}>{renderQuestion(q)}</div>
                                    ))}
                                    <button type="submit" className="btn btn-primary mt-3">
                                        Submit Survey
                                    </button>
                                </div>
                            )}
                        </form>
                    </Paper>
                </div>

                {/* Watched Values - 30% */}
                <div className="col-4">
                    <div className="card" style={{ borderRadius: '10px' }}>
                        <div className="card-header">
                            <h5 className="mb-0">Watched Values</h5>
                        </div>
                        <div className="card-body">
                            <pre style={{ fontSize: '12px', maxHeight: '500px', overflow: 'auto' }}>
                                {JSON.stringify(formValues, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        </Box>
    );
};

export default EcxSurveyViewer;
